/**
 * vendorFamilies.js — Pure vendor-family grouping over the classifier's real
 * `row.requestType` vocabulary (docs/checks-adscope-spec.md §6, WP3).
 *
 * MIRRORS proxy-server/conformance/requestTypes.js — keep in sync (known
 * frontend↔proxy duplication, same pattern as knowledge packs).
 *
 * requestTypes.js is the proxy's single source of truth for the *classifier*
 * vocabulary (CLASSIFIER_REQUEST_TYPES — what actually lands in row.requestType)
 * and for how a Check's `target.requestType` may name a vendor with a shorthand
 * word (REQUEST_TYPE_ALIASES: gam/freewheel/springserve — the compile-time
 * `namedFamilies`/`enforceNamedFamily` machinery, WP0). That table intentionally
 * has NO reverse "real classifier type -> display family" grouping (it only
 * expands shorthand alias words), and deliberately excludes ambiguous
 * traffic-shape words like "video"/"omid" from vendor-name inference.
 *
 * This module adds that reverse grouping for the Checks page's vendor chips —
 * a DISPLAY concern (bucket the classifier types actually seen in a session
 * into a small set of vendor/format chips), not a compile-time NL-inference
 * concern. FAMILY_REQUEST_TYPES below is built by hand from the real
 * CLASSIFIER_REQUEST_TYPES list (proxy-server/conformance/requestTypes.js,
 * verified 2026-08-09) plus utils.js's determineRequestType() rule comments —
 * grouped by vendor/product, not invented: every classifier type appears in
 * exactly one family or falls through to 'other'. The 'video' family is an
 * intentional addition beyond requestTypes.js's named-family set (which
 * excludes "video" as ambiguous for compile-time scope injection) — the
 * spec's own §6 example ("GAM 214, Prebid 89, APS 41, VAST 12") calls for a
 * VAST chip, and vast_error/vast_tracking/video_content are vendor-agnostic
 * VAST-protocol signals worth their own bucket rather than dumping into
 * 'other'. gam_video stays under 'google' — it's GAM's own video ad request,
 * not a generic VAST signal.
 */

// family -> real classifier requestType strings (proxy CLASSIFIER_REQUEST_TYPES)
// that belong to it. Every array member here must be a real classifier type
// (verify against proxy-server/conformance/requestTypes.js if this is ever
// suspected stale — don't trust a copy of a copy).
export const FAMILY_REQUEST_TYPES = {
  google: ['google', 'google_click', 'google_impression', 'gam_video', 'activeview_measurement', 'publisher_settings'],
  prebid: ['prebid', 'prebid_win', 'bidder_impression'],
  amazon: ['amazon', 'amazon_video', 'amazon_impression'],
  ima: ['ima'],
  video: ['vast_error', 'vast_tracking', 'video_content'],
  sdk_bidding: ['sdk_bidding'],
  freewheel: ['freewheel_ad', 'freewheel_tracking'],
  springserve: ['springserve_ad_request', 'springserve_vast'],
};

// Display label per family, including the 'other' catch-all bucket.
export const FAMILY_LABELS = {
  google: 'GAM',
  prebid: 'Prebid',
  amazon: 'APS',
  ima: 'IMA',
  video: 'VAST',
  sdk_bidding: 'SDK Bidding',
  freewheel: 'FreeWheel',
  springserve: 'SpringServe',
  other: 'Other ad',
};

// Shorthand words a Check's target.requestType may carry instead of a literal
// classifier type — mirrors requestTypes.js's REQUEST_TYPE_ALIASES (gam,
// freewheel, springserve) plus its FAMILY_WORD_PATTERNS EXTRA synonyms
// (doubleclick/google ad manager, aps/tam, sdk bidding/mediation). Keys are
// lowercase; the alias word itself maps directly to its canonical family.
const ALIAS_WORD_TO_FAMILY = {
  gam: 'google',
  doubleclick: 'google',
  google: 'google',
  aps: 'amazon',
  tam: 'amazon',
  amazon: 'amazon',
  prebid: 'prebid',
  ima: 'ima',
  mediation: 'sdk_bidding',
  sdk_bidding: 'sdk_bidding',
  freewheel: 'freewheel',
  springserve: 'springserve',
};

// The pseudo-family the "All" chip represents — never a real bucket key.
export const ALL_FAMILY = 'all';

// Built once at module load: real classifier requestType -> family.
const REQUEST_TYPE_TO_FAMILY = (() => {
  const out = {};
  for (const [family, types] of Object.entries(FAMILY_REQUEST_TYPES)) {
    for (const t of types) out[t] = family;
  }
  return out;
})();

/**
 * familyOfRequestType(rt) -> family key | null
 * Maps a real classifier row.requestType string to its vendor family. Returns
 * null for an unknown/unmapped type (including context pseudo-types like
 * 'content'/'login', and non-string/empty input) — callers bucket that under
 * 'other'. Never throws.
 */
export function familyOfRequestType(rt) {
  try {
    if (typeof rt !== 'string' || !rt) return null;
    return REQUEST_TYPE_TO_FAMILY[rt] || null;
  } catch {
    return null;
  }
}

/**
 * tallyFamilies(input) -> [{ family, label, count }] sorted by count desc
 *
 * `input` is either:
 *   - a requestType tally, e.g. POST /proxy/checks/introspect's echoed
 *     `requestTypes: [{ type, count }]` (the cheapest already-computed
 *     source — prefer this over iterating the full row set), or
 *   - raw traffic rows, e.g. `[{ requestType, ... }, ...]`
 * Item shape is detected per-entry (a `type` string wins over a `requestType`
 * string), so a mixed/malformed array degrades gracefully rather than
 * throwing. Types that don't map to a known family (familyOfRequestType ->
 * null) are bucketed under 'other'. Non-array/empty input -> []. Never throws.
 */
export function tallyFamilies(input) {
  try {
    const list = Array.isArray(input) ? input : [];
    const counts = new Map(); // family -> count
    for (const item of list) {
      if (!item || typeof item !== 'object') continue;
      let type = null;
      let count = 1;
      if (typeof item.type === 'string' && item.type) {
        type = item.type;
        count = Number.isFinite(Number(item.count)) ? Number(item.count) : 1;
      } else if (typeof item.requestType === 'string' && item.requestType) {
        type = item.requestType;
        count = 1;
      }
      if (!type) continue;
      const family = familyOfRequestType(type) || 'other';
      counts.set(family, (counts.get(family) || 0) + count);
    }
    return Array.from(counts.entries())
      .map(([family, count]) => ({ family, label: FAMILY_LABELS[family] || family, count }))
      .sort((a, b) => b.count - a.count);
  } catch {
    return [];
  }
}

/** True when `word` (a literal classifier type OR a shorthand alias word) maps to `family`. */
function wordMapsToFamily(word, family) {
  if (typeof word !== 'string' || !word) return false;
  const w = word.toLowerCase();
  if (ALIAS_WORD_TO_FAMILY[w] === family) return true;
  return familyOfRequestType(word) === family;
}

/**
 * checkMatchesFamily(check, family) -> boolean
 *
 * `family` === ALL_FAMILY ('all') or falsy -> always true (the "All" chip
 * shows everything, unfiltered).
 *
 * Otherwise:
 *   1. If `check.target.requestType` is set (a literal classifier type, a
 *      shorthand alias word like 'gam', or an array of either) — true when
 *      ANY of those words/types expands/maps into `family`.
 *   2. If the check has NO requestType constraint (`target` missing, or
 *      `target.requestType` absent/null/empty — including pure-entity
 *      targets like slot/orphan/seller/videoSession checks, which never
 *      carry requestType), it does NOT match a specific family chip — a
 *      broad/unconstrained check only ever shows under "All", never under
 *      every vendor chip. The one exception: a runSuite RESULT object (the
 *      shape `baselineReport.results[i]` actually has — evaluateCheck.js
 *      never echoes `target` back, only `checkId`/`rows`/`evidence`) is
 *      attributed empirically from the rows it actually matched
 *      (`check.rows[].requestType` / `check.evidence[].row.requestType` —
 *      see evaluateCheck.js's buildEvidenceRow) when present, so baseline
 *      auto-run results can still be filtered by family with zero extra
 *      endpoints. A result with no attributable evidence still falls back
 *      to "All only".
 *
 * Defensive throughout — malformed `check`/`family` never throws, just
 * yields false (aside from the ALL_FAMILY short-circuit above).
 */
export function checkMatchesFamily(check, family) {
  try {
    if (!family || family === ALL_FAMILY) return true;
    if (!check || typeof check !== 'object') return false;

    const target = (check.target && typeof check.target === 'object' && !Array.isArray(check.target))
      ? check.target
      : null;
    const rt = target ? target.requestType : undefined;
    const words = Array.isArray(rt)
      ? rt
      : (typeof rt === 'string' && rt.trim() ? [rt] : []);

    if (words.length > 0) {
      return words.some((w) => wordMapsToFamily(w, family));
    }

    // No target.requestType on the object itself — try the empirical
    // fallback for runSuite result shapes (see doc comment above).
    const evidenceRows = [];
    if (Array.isArray(check.rows)) evidenceRows.push(...check.rows);
    if (Array.isArray(check.evidence)) {
      for (const e of check.evidence) if (e && e.row) evidenceRows.push(e.row);
    }
    if (evidenceRows.length === 0) return false;
    return evidenceRows.some((r) => familyOfRequestType(r && r.requestType) === family);
  } catch {
    return false;
  }
}
